import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { MagnifyingGlass, CaretDown } from "@phosphor-icons/react";

export function CountryPicker({ value, onChange }) {
  const { data } = useQuery({
    queryKey: ["catalog-countries"],
    queryFn: async () => (await api.get("/catalog/countries")).data,
    staleTime: 60 * 60 * 1000,
  });
  const countries = useMemo(() => data?.countries || [], [data]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const selected = countries.find((c) => c.code === value);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return countries;
    return countries.filter((c) =>
      c.name.toLowerCase().includes(s) || c.code.toLowerCase() === s || (c.dial || "").includes(s));
  }, [q, countries]);

  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid="country-picker-trigger" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-xl border border-input bg-card px-3 py-2.5 text-left text-sm outline-none focus:border-primary">
        {selected ? (
          <>
            <img src={selected.flag} alt="" className="h-4 w-6 shrink-0 rounded-sm object-cover ring-1 ring-black/5" />
            <span className="flex-1 truncate font-medium">{selected.name}</span>
            <span className="text-muted-foreground">{selected.dial}</span>
          </>
        ) : <span className="flex-1 text-muted-foreground">Select a country</span>}
        <CaretDown size={16} className="shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-border bg-card shadow-xl">
          <div className="sticky top-0 flex items-center gap-2 border-b border-border bg-card px-3 py-2">
            <MagnifyingGlass size={16} className="text-muted-foreground" />
            <input autoFocus data-testid="country-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search countries…" className="w-full bg-transparent text-sm outline-none" />
          </div>
          {filtered.map((c) => (
            <button key={c.code} type="button" data-testid={`country-opt-${c.code}`}
              onClick={() => { onChange(c); setOpen(false); setQ(""); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${value === c.code ? "bg-accent" : ""}`}>
              <img src={c.flag} alt="" loading="lazy" className="h-4 w-6 shrink-0 rounded-sm object-cover ring-1 ring-black/5" />
              <span className="flex-1 truncate">{c.name}</span>
              <span className="text-xs text-muted-foreground">{c.dial}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</p>}
        </div>
      )}
    </div>
  );
}
